/* eslint-disable max-lines */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AvcActions, ErrorMessage } from './enum';
import {
    addressSaga,
    handleTivanoResponse,
    seamlessAVCFromCache,
    tivanoSaga,
    updateOffersAfterVpSaga,
} from './util/generator';
import { debugInfo } from '../../util/debug';
import { setLoading, setError, setAddresses, setAccessSupplier, setAvailabilityIndicator } from './action';
import { setHomesPassed, setLoading as setLpLoading, setTariffs } from '../App/action';
import { selectHomesPassed, selectTariff } from '../App/selector';
import { trackError, trackPageView } from '../../util/tracking';
import { TrackingAvcStep } from '../../enums/tracking';
import { setAvcResult } from '../App/action';
import { deleteCookie, isProdEnv, setCookie } from '@vfde-sails/utils';
import { Cookies } from '../../enums/network';
import {
    Address,
    GimliData,
    JarvisData,
    JarvisProduct,
    TivanoData,
    getGimliData,
    toAvcResult,
    getJarvisSuggestionData,
    AvcSessionEntry,
    AvcResult,
} from '@vfde-sails/avc-core';
import { selectAddress } from '../App/selector';
import { isAvcError, isHomesPassedMarker, isJarvisProductArray } from './util/typeGuard';
import { ST_COOKIE_DOMAIN, VF_COOKIE_DOMAIN, ZHP_NO_FIBER_TRIGGER } from './constants';
import { Action } from 'redux';
import { filterOnlyCableMetacodes, isPreFiberAvailable } from './util/jarvisHelper';
import { isSTEnv } from '../../util/url';
import { trackPageViewSaga } from 'Container/App/saga';
import { HomesPassedType } from '../../enums/product';

export function* seamlessAvc(seamlessAvcResult: AvcSessionEntry | null) {
    if (!seamlessAvcResult?.result || !seamlessAvcResult?.customProps) {
        return null;
    }

    const { result, customProps } = seamlessAvcResult;
    const [sessionTariffs, sessionHomesPassed] = customProps;

    const tariffs = isJarvisProductArray(sessionTariffs) ? sessionTariffs : null;

    if (!tariffs) {
        return null;
    }

    const homesPassed = isHomesPassedMarker(sessionHomesPassed) ? sessionHomesPassed : null;

    if (homesPassed) {
        yield put(setHomesPassed(homesPassed));
    }

    yield put(setTariffs(tariffs));
    yield put(setAvcResult(result as AvcResult));
    yield put(setAccessSupplier(result.accessSupplier));
    yield put(setAvailabilityIndicator(result.availabilityIndicator));

    return true;
}

export function* availabilityCheckSaga(_?: Action, isSeamless = false) {
    try {
        const selectedTariff: JarvisProduct = yield select(selectTariff);

        if (!isSeamless) {
            yield call(trackPageView, selectedTariff, undefined, TrackingAvcStep.START);
        }

        // Seamless AVC from cache
        const seamlessAvcResult: AvcSessionEntry | null = isSeamless
            ? yield call(seamlessAVCFromCache)
            : null;
        const isSeamlessAvcSuccessful: boolean | null =
            yield call(seamlessAvc, seamlessAvcResult);

        if (isSeamlessAvcSuccessful) {
            return;
        }

        // Regular AVC
        yield put(setLoading());
        yield put(setError());

        const address: Address | undefined = yield call(addressSaga);

        if (!address) {
            throw new Error(ErrorMessage.EMPTY_RESPONSE);
        }

        const gimli: GimliData = yield call(getGimliData, address);
        const tivano: TivanoData = yield call(tivanoSaga, address, isSeamless);
        const homePassed: HomesPassedType = yield select(selectHomesPassed);
        // TODO: adjust
        if (!tivano || !gimli) {
            throw new Error(ErrorMessage.EMPTY_RESPONSE);
        }

        const {
            accessSupplier,
            availabilityIndicator,
        } = yield call(handleTivanoResponse, tivano);

        const jarvis: JarvisData = yield call(getJarvisSuggestionData, {
            salesChannel: 'PRE_FIB',
            selectedId: selectedTariff.id,
            productId: [
                ...filterOnlyCableMetacodes(Object.keys(gimli.products)),
                ...tivano.metacodes,
            ],
            gimli: gimli.products,
            tivano: tivano.products,
            accessSupplier,
            availabilityIndicator,
        });
        const avcResult = toAvcResult(gimli, tivano, jarvis);
        const isPreFiberCase = isPreFiberAvailable(avcResult.fastest?.id);

        if (isPreFiberCase) {
            yield call(updateOffersAfterVpSaga, tivano.products);
        }

        if (avcResult && !avcResult.availability.fiber) {
            if (isProdEnv()) {
                setCookie(Cookies.ZHP_LM_MODULE, {
                    value: ZHP_NO_FIBER_TRIGGER,
                    domain: VF_COOKIE_DOMAIN,
                });
            } else if (isSTEnv()) {
                setCookie(Cookies.ZHP_LM_MODULE, {
                    value: ZHP_NO_FIBER_TRIGGER,
                    domain: ST_COOKIE_DOMAIN,
                });
            } else {
                setCookie(Cookies.ZHP_LM_MODULE, {
                    value: ZHP_NO_FIBER_TRIGGER,
                    domain: '',
                });
            }

        } else {
            deleteCookie(Cookies.ZHP_LM_MODULE);
        }

        yield put(setAvcResult(avcResult));

        if (isProdEnv()) {
            setCookie(Cookies.ADDRESS, {
                value: btoa(JSON.stringify(address)),
                domain: VF_COOKIE_DOMAIN,
            });
        } else if (isSTEnv()) {
            setCookie(Cookies.ADDRESS, {
                value: btoa(JSON.stringify(address)),
                domain: ST_COOKIE_DOMAIN,
            });
        } else {
            setCookie(Cookies.ADDRESS, {
                value: btoa(JSON.stringify(address)),
                domain: '',
            });
        }
        yield call(trackPageViewSaga, isSeamless ? undefined : TrackingAvcStep.COMPLETE,
            accessSupplier, availabilityIndicator, homePassed);
    } catch (error) {
        debugInfo('Error in avc flow: ', error);

        yield put(setError(error as Error));

        if (isAvcError(error)) {
            yield call(trackError, error, true);
        }
    } finally {
        yield put(setLoading(false));
    }
}

export function* getEntryStateSaga() {
    const address: Address = yield select(selectAddress);

    if (!address) {
        yield call(trackPageViewSaga);
        return;
    }

    // Seamless check starts when there is already an address
    yield put(setLpLoading());
    yield put(setAddresses([address]));
    yield call(availabilityCheckSaga, undefined, true);
    yield put(setLpLoading(false));
}

/**
 * Availability check saga
 *
 * @export {function} saga
 */
export default function* saga() {
    yield takeLatest(AvcActions.GET_ENTRY_STATE, getEntryStateSaga);
    yield takeLatest(AvcActions.CHECK_ADDRESS, availabilityCheckSaga);
}
